import React, { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import { database } from '../../Firebase';
import { ref, onValue } from 'firebase/database';

const Namuna9Report = () => {
  const { id } = useParams();
  const [record, setRecord] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (id) {
      const recordRef = ref(database, `Grampanchayat/Gramniddhi/NewMilakhatRecords/${id}`);
      onValue(recordRef, (snapshot) => {
        const data = snapshot.val();
        setRecord(data);
        setLoading(false);
      });
    }
  }, [id]);

  if (loading) return <div className="p-4 text-center">Loading...</div>;
  if (!record) return <div className="p-4 text-center text-red-500">Record not found</div>;

  const handlePrint = () => {
    window.print();
  };

  const taxes = record.taxData || {};
  const houseTax = taxes.houseTax || { current: 0, previous: 0, total: 0 };
  const healthTax = taxes.healthTax || { current: 0, previous: 0, total: 0 };
  const lightTax = taxes.electricityTax || { current: 0, previous: 0, total: 0 };

  // Calculations for Totals
  const totalPrevious = (parseInt(houseTax.previous) || 0) + (parseInt(lightTax.previous) || 0) + (parseInt(healthTax.previous) || 0);
  const totalCurrent = (parseInt(houseTax.current) || 0) + (parseInt(lightTax.current) || 0) + (parseInt(healthTax.current) || 0);
  const grossTotal = totalPrevious + totalCurrent; // Needs to be granular per tax type usually, but simplifed here based on available data

  // Note: The screenshot implies granular columns.
  // Demand:
  // House: P, C, T
  // Light: P, C, T
  // Health: P, C, T
  // Penalty: -
  // Total: Sum of T's
  // Discount: -
  // Final Total: Total - Discount

  const houseTotal = (parseInt(houseTax.previous) || 0) + (parseInt(houseTax.current) || 0);
  const lightTotal = (parseInt(lightTax.previous) || 0) + (parseInt(lightTax.current) || 0);
  const healthTotal = (parseInt(healthTax.previous) || 0) + (parseInt(healthTax.current) || 0);

  const finalTotal = houseTotal + lightTotal + healthTotal;

  return (
    <div id="printable-content" className="bg-white min-h-screen p-4 text-[11px] font-serif text-black leading-tight">
      <style>{`
        @media print {
          body * {
            visibility: hidden;
          }
          body {
            -webkit-print-color-adjust: exact;
          }
          #printable-content, #printable-content * {
            visibility: visible;
          }
          #printable-content {
            position: absolute;
            left: 0;
            top: 0;
            width: 100%;
          }
          .print-border {
            border: 2px solid black !important;
          }
          @page { size: landscape; margin: 5mm; }
          .no-print { display: none !important; }
        }
        table { border-collapse: collapse; width: 100%; }
        th, td { border: 1px solid black; padding: 4px; vertical-align: middle; text-align: center; }
      `}</style>

      {/* Print Button */}
      <div className="max-w-screen-xl mx-auto mb-4 no-print flex justify-end">
        <button
          onClick={handlePrint}
          className="bg-blue-600 text-white px-4 py-2 rounded hover:bg-blue-700 font-bold"
        >
          Print / Save PDF
        </button>
      </div>

      <div className="w-fit mx-auto border-2 border-black p-1 print-border">

        {/* Header Block */}
        <div className="flex border-b-2 border-black">
          {/* Left: Ward/Circle info placeholder */}
          <div className="w-[15%] border-r border-black p-2 text-left">
            <p>वॉर्ड - </p>
            <p className="mt-2">सर्कल - </p>
          </div>

          {/* Center: Title */}
          <div className="w-[70%] border-r border-black text-center p-2">
            <h1 className="text-xl font-bold">नमुना नं. ९</h1>
            <p className="mt-1 font-semibold">सन २०२५ -२०२६ च्या आकारणी केलेल्या करांच्या मागणीचे नोंदणीपुस्तक</p>
            <h2 className="text-lg font-bold mt-1">ग्रामपंचायत इनाम धामणी , तालुका मिरज , जिल्हा सांगली.</h2>
          </div>

          {/* Right: Notes */}
          <div className="w-[15%] p-2 text-[9px] text-left">
            <p>सुट देण्यास मंजुरी देणा-या हुकुमाचे उल्लेख व शेरे देणे</p>
            <p>टीप - शेरे व दुरुस्त्या यावर त्यांच्या खरेपणाबद्दल सरपंचाने सही केली पाहीजे.</p>
          </div>
        </div>

        {/* Main Table */}
        <table className="w-full">
          <thead>
            <tr className="bg-gray-100">
              <th rowSpan="3" className="w-8">अ.क्र.</th>
              <th rowSpan="3" className="w-16">मालमत्ता क्रमांक</th>
              <th rowSpan="3" className="w-48">ज्या इसमाकडून येणे असेल त्यांची नावे</th>
              <th rowSpan="3" className="w-24">भोगवटा करणाऱ्याचे नाव</th>

              {/* Demand Section */}
              <th colSpan="13">मागणी</th>

              {/* Recovery Section */}
              <th colSpan="9">वसुली</th>
            </tr>
            <tr className="bg-gray-100">
              {/* Demand Sub-headers */}
              <th colSpan="3">घरपट्टी</th>
              <th colSpan="3">विजकर</th>
              <th colSpan="3">आरोग्य कर</th>
              <th rowSpan="2" className="w-10">घ.मा.दंड</th>
              <th rowSpan="2" className="w-12">एकुण</th>
              <th rowSpan="2" className="w-10">घ.चा.सुट</th>
              <th rowSpan="2" className="w-12">एकुण</th>

              {/* Recovery Sub-headers */}
              <th colSpan="2">घरपट्टी</th>
              <th colSpan="2">विजकर</th>
              <th colSpan="2">आरोग्य कर</th>
              <th rowSpan="2">एकुण</th>
              <th rowSpan="2">पावती क्र.</th>
            </tr>
            <tr className="bg-gray-100 text-[10px]">
              {/* Demand sub-cols */}
              <th>मागील</th><th>चालू</th><th>एकुण</th>
              <th>मागील</th><th>चालू</th><th>एकुण</th>
              <th>मागील</th><th>चालू</th><th>एकुण</th>

              {/* Recovery sub-cols */}
              <th>मा</th><th>चा</th>
              <th>मा</th><th>चा</th>
              <th>मा</th><th>चा</th>
            </tr>
            {/* Row 4 (Receipt Date below Receipt No usually, or separate col? Image shows it below Receipt No header in one cell or next row. Image has 'पावती दिनांक' under 'पावती क्र.' cell visually or next to it? 
                 Ah, looking at image: 'पावती क्र.' is top, 'पावती दिनांक' is bottom of that cell split? Or separate column?
                 Actually looks like last column is split vertically: Top: Payti Kr, Bottom: Payti Dinank? No, it looks like a separate row 4 for numbering?
                 Wait, the image shows a sub-row for 'पावती दिनांक' below 'पावती क्र.'? No, it's just a column header.
                 Let's stick to standard column.
                 */}
            <tr className="bg-white text-[9px] h-4">
              {/* Numbering row if needed, skipped for simplicity/image didn't strictly show numbers 1-26 explicitly like Form 8? Actually image has 1 2 ... row. */}
              <th>1</th><th>2</th><th></th><th></th>
              {/* ... */}
              <td colSpan="22"></td>
            </tr>
          </thead>
          <tbody>
            <tr className="h-12">
              <td>1</td>
              <td>{record.propertyNo}</td>
              <td className="text-left px-2 font-bold">{record.mainOwner}</td>
              <td>{record.occupierName || 'स्वत:'}</td>

              {/* Demand Data */}
              {/* House */}
              <td>{houseTax.previous || '-'}</td>
              <td>{houseTax.current}</td>
              <td>{houseTotal}</td>

              {/* Light */}
              <td>{lightTax.previous || '-'}</td>
              <td>{lightTax.current}</td>
              <td>{lightTotal}</td>

              {/* Health */}
              <td>{healthTax.previous || '-'}</td>
              <td>{healthTax.current}</td>
              <td>{healthTotal}</td>

              <td>-</td> {/* Penalty */}
              <td>{finalTotal}</td> {/* Gross Total */}
              <td>-</td> {/* Discount */}
              <td className="font-bold">{finalTotal}</td> {/* Net Total */}

              {/* Recovery Data - Empty for now */}
              <td></td><td></td>
              <td></td><td></td>
              <td></td><td></td>
              <td></td>
              <td></td>
            </tr>

            {/* Total Row */}
            <tr className="font-bold bg-gray-50">
              <td colSpan="4" className="text-right px-2">एकुण</td>
              {/* House */}
              <td>{houseTax.previous || '-'}</td>
              <td>{houseTax.current}</td>
              <td>{houseTotal}</td>
              {/* Light */}
              <td>{lightTax.previous || '-'}</td>
              <td>{lightTax.current}</td>
              <td>{lightTotal}</td>
              {/* Health */}
              <td>{healthTax.previous || '-'}</td>
              <td>{healthTax.current}</td>
              <td>{healthTotal}</td>

              <td>-</td>
              <td>{finalTotal}</td>
              <td>-</td>
              <td>{finalTotal}</td>

              <td colSpan="9"></td>
            </tr>

          </tbody>
        </table>

      </div>
    </div>
  );
};

export default Namuna9Report;
